import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';
import { sortFindings } from '../../../utils/reportSharedUtils';

interface RemediationRoadmapPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  footerLeft: {
    textAlign: 'left',
  },
  footerRight: {
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 16,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 16,
    color: '#475569',
    fontSize: 11.5,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 36,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 8,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  idPill: {
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 6,
    alignSelf: 'center',
  },
  idPillText: {
    color: '#475569',
    fontSize: 8,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  priorityPill: {
    borderRadius: 10,
    paddingVertical: 3.5,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 62,
    flexDirection: 'row',
  },
  priorityPillText: {
    fontSize: 7.5,
    fontWeight: 'bold',
    lineHeight: 1.1,
    textAlign: 'center',
  },
  noFindingsBox: {
    padding: 20,
    textAlign: 'center',
    color: '#16A34A',
    fontWeight: 'bold',
    borderWidth: 1,
    borderColor: '#D1FAE5',
    backgroundColor: '#ECFDF5',
    borderRadius: 6,
    marginTop: 12,
  },
});

const RemediationRoadmapPageV2: React.FC<RemediationRoadmapPageProps> = ({ reportData, sectionId }) => {
  const rawFindings = reportData.detailed_findings || [];
  const openFindings = rawFindings.filter(f => f.status?.toLowerCase() === 'open');
  const findings = sortFindings(openFindings);

  const priorityColors: Record<string, { bg: string; text: string }> = {
    'Immediate': { bg: '#E11D48', text: '#FFFFFF' },
    'Short term': { bg: '#F97316', text: '#FFFFFF' },
    'Medium term': { bg: '#EAB308', text: '#1F2937' },
    'Low term': { bg: '#16A34A', text: '#FFFFFF' },
    'Low': { bg: '#16A34A', text: '#FFFFFF' },
    'Info': { bg: '#8B9BB4', text: '#FFFFFF' }
  };

  const getRemediationDetails = (sev: string) => {
    switch (sev) {
      case 'Critical':
        return { priority: 'Immediate', timeframe: '7 Days' };
      case 'High':
        return { priority: 'Short term', timeframe: '30 Days' };
      case 'Medium':
        return { priority: 'Medium term', timeframe: '90 Days' };
      case 'Low':
        return { priority: 'Short term', timeframe: '180 Days' };
      default:
        return { priority: 'Info', timeframe: 'Best Effort' };
    }
  };

  const getTargetDate = (timeframe: string, baseDate: any) => {
    if (!timeframe) return '25 Jun 2026';
    // If it's already a date format, return it
    if (timeframe.match(/^\d{1,2}\s+[a-zA-Z]{3}\s+\d{4}$/)) {
      return timeframe;
    }
    const date = baseDate ? new Date(baseDate) : new Date();
    if (isNaN(date.getTime())) return timeframe;
    
    let daysToAdd = 3;
    if (timeframe.toLowerCase().includes('7')) daysToAdd = 3;
    else if (timeframe.toLowerCase().includes('30')) daysToAdd = 30;
    else if (timeframe.toLowerCase().includes('90') || timeframe.toLowerCase().includes('medium')) daysToAdd = 90;
    else if (timeframe.toLowerCase().includes('180') || timeframe.toLowerCase().includes('low')) daysToAdd = 180;
    
    date.setDate(date.getDate() + daysToAdd);
    
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Title */}
      <Text style={styles.sectionTitle}>8. Remediation Roadmap</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      {/* Sub-text */}
      <Text style={styles.paragraph}>
        Consolidated, prioritized action plan. Ownership and target dates to be agreed with the client.
      </Text>

      {findings.length === 0 ? (
        <View style={styles.noFindingsBox}>
          <Text>No remediation actions required.</Text>
        </View>
      ) : (
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>ID</Text>
            <Text style={[styles.tableHeaderCell, { width: '40%' }]}>Action</Text>
            <Text style={[styles.tableHeaderCell, { width: '15%', textAlign: 'center' }]}>Priority</Text>
            <Text style={[styles.tableHeaderCell, { width: '15%' }]}>Owner</Text>
            <Text style={[styles.tableHeaderCell, { width: '15%' }]}>Target</Text>
          </View>
          {findings.map((f, idx) => {
            const fid = f.report_id || f.id || `F-${idx + 1}`;
            const sev = f.severity_category || 'Info';
            const { priority: defaultPriority, timeframe: defaultTimeframe } = getRemediationDetails(sev);
            
            // Format action to show a clean summary action text
            const defaultAction = f.title ? `Remediate ${f.title}` : (f.fix ? f.fix.replace(/<[^>]*>/g, '').split(/[.!?]/)[0] + '.' : 'Remediate vulnerability');
            const defaultOwner = 'App Team';

            const overrides = reportData.finding_config?.remediationRoadmap?.[fid] || {};
            const action = overrides.action !== undefined ? overrides.action : defaultAction;
            const priority = overrides.priority !== undefined ? overrides.priority : defaultPriority;
            const timeframe = overrides.timeline !== undefined ? overrides.timeline : defaultTimeframe;
            const owner = overrides.owner !== undefined ? overrides.owner : defaultOwner;

            const baseDate = reportData.end_date || reportData.start_date || reportData.current_date;
            const targetDate = getTargetDate(timeframe, baseDate);
            const pillColors = priorityColors[priority] || priorityColors.Info;
            const isLast = idx === findings.length - 1;

            return (
              <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
                {/* ID Pill */}
                <View style={[styles.tableRowCell, { width: '15%', alignItems: 'center', justifyContent: 'center' }]}>
                  <View style={styles.idPill}>
                    <Text style={styles.idPillText}>{fid}</Text>
                  </View>
                </View>

                {/* Action summary */}
                <Text style={[styles.tableRowCell, { width: '40%', color: '#1F2937' }]}>
                  {action}
                </Text>

                {/* Priority Pill */}
                <View style={[styles.tableRowCell, { width: '15%', alignItems: 'center', justifyContent: 'center' }]}>
                  <View style={[styles.priorityPill, { backgroundColor: pillColors.bg }]}>
                    <Text style={[styles.priorityPillText, { color: pillColors.text }]}>{priority}</Text>
                  </View>
                </View>

                {/* Owner */}
                <Text style={[styles.tableRowCell, { width: '15%', color: '#1F2937' }]}>
                  {owner}
                </Text>

                {/* Target Date */}
                <Text style={[styles.tableRowCell, { width: '15%', color: '#1F2937' }]}>
                  {targetDate}
                </Text>
              </View>
            );
          })}
        </View>
      )}
      {/* Footer page number */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default RemediationRoadmapPageV2;
